import React from 'react';
import {
  Box,
  Button,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import SalonButtonLayoutControls from './SalonButtonLayoutControls';
import SalonContentLayoutControls from './SalonContentLayoutControls';
import SalonHeaderLayoutControls from './SalonHeaderLayoutControls';
import SalonSectionStyleControls from './SalonSectionStyleControls';
import SalonSectionVisibilityControls from './SalonSectionVisibilityControls';
import SalonThemeSections from './SalonThemeSections';


export const getAdvancedGroupForField = (fieldPath) => {
  if (fieldPath?.startsWith('buttons.')) return 'buttons';
  if (fieldPath?.startsWith('header.')) return 'header';
  if (fieldPath?.startsWith('content.')) return 'content';
  if (
    fieldPath?.startsWith('visibility.')
    || fieldPath?.startsWith('section_styles.')
  ) {
    return 'sections';
  }
  return null;
};

const SalonThemeAdvancedControls = ({
  appearance,
  capabilities,
  errors = {},
  expandedGroup,
  onExpandedGroupChange,
  onBack,
  onLayoutFieldChange,
  onSectionStyleFieldChange,
  onResetSection,
  onResetSaloone,
  expandedStyleSection,
  onExpandedStyleSectionChange,
  disabled = false,
  registerFieldRef = () => {},
}) => {
  const { t } = useTranslation();
  const layout = appearance?.layout || {};
  const theme = appearance?.theme;
  const buttonsGroup = {
    key: 'buttons',
    label: t('salon_theme.sections.buttons'),
    content: (
      <SalonButtonLayoutControls
        value={layout.buttons}
        theme={theme}
        errors={errors.buttons}
        onChange={(field, value) => onLayoutFieldChange('buttons', field, value)}
        disabled={disabled}
        registerFieldRef={(field, element) => registerFieldRef(`buttons.${field}`, element)}
      />
    ),
  };
  const headerGroup = {
    key: 'header',
    label: t('salon_theme.sections.header'),
    content: (
      <SalonHeaderLayoutControls
        value={layout.header}
        errors={errors.header}
        onChange={(field, value) => onLayoutFieldChange('header', field, value)}
        disabled={disabled}
        registerFieldRef={(field, element) => registerFieldRef(`header.${field}`, element)}
      />
    ),
  };
  const contentGroup = {
    key: 'content',
    label: t('salon_theme.sections.content'),
    content: (
      <SalonContentLayoutControls
        value={layout.content}
        errors={errors.content}
        onChange={(field, value) => onLayoutFieldChange('content', field, value)}
        disabled={disabled}
        registerFieldRef={(field, element) => registerFieldRef(`content.${field}`, element)}
      />
    ),
  };
  const sectionsGroup = {
    key: 'sections',
    label: t('salon_theme.advanced.sections'),
    content: (
      <Stack spacing={3}>
        <Box>
          <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
            {t('salon_theme.sections.visibility')}
          </Typography>
          <SalonSectionVisibilityControls
            value={layout.visibility}
            errors={errors.visibility}
            onChange={(field, value) => onLayoutFieldChange('visibility', field, value)}
            disabled={disabled}
            registerFieldRef={(field, element) => registerFieldRef(`visibility.${field}`, element)}
          />
        </Box>
        <Box>
          <Typography component="h3" variant="subtitle1" sx={{ fontWeight: 800, mb: 1 }}>
            {t('salon_theme.sections.section_styles')}
          </Typography>
          <SalonSectionStyleControls
            value={layout.section_styles}
            theme={theme}
            errors={errors.section_styles}
            onChange={onSectionStyleFieldChange}
            onResetSection={onResetSection}
            disabled={disabled}
            expandedKey={expandedStyleSection}
            onExpandedChange={onExpandedStyleSectionChange}
            registerFieldRef={(field, element) => registerFieldRef(`section_styles.${field}`, element)}
          />
        </Box>
      </Stack>
    ),
  };
  const groups = [
    buttonsGroup,
    ...(capabilities?.structuralControls ? [headerGroup, contentGroup] : []),
    sectionsGroup,
  ];
  const handleExpandedGroupChange = (nextGroup) => {
    onExpandedGroupChange(nextGroup === expandedGroup ? null : nextGroup);
  };

  return (
    <Stack spacing={2.5}>
      <Button type="button" variant="text" onClick={onBack} disabled={disabled}>
        {t('salon_theme.advanced.back')}
      </Button>
      <SalonThemeSections
        sections={groups}
        expandedKey={expandedGroup}
        onExpandedChange={handleExpandedGroupChange}
        disabled={disabled}
      />
      <Divider />
      <Button
        type="button"
        variant="outlined"
        color="inherit"
        onClick={onResetSaloone}
        disabled={disabled}
        sx={{ alignSelf: 'flex-start' }}
      >
        {t('salon_theme.advanced.reset_saloone')}
      </Button>
    </Stack>
  );
};

export default SalonThemeAdvancedControls;
